export type CdfJson = {
  grid_cm: number[];
  percentiles: number[]; // 0..100 mapping to grid
  meta: {
    mean_cm: number;
    median_cm: number;
    std_cm?: number;
    n: number;
    note?: string;
  };
};

export function interpPercentile(cdf: CdfJson, heightCm: number): number {
  const { grid_cm, percentiles } = cdf;
  if (heightCm <= grid_cm[0]) return percentiles[0];
  if (heightCm >= grid_cm[grid_cm.length - 1])
    return percentiles[percentiles.length - 1];
  // binary search
  let lo = 0,
    hi = grid_cm.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (grid_cm[mid] === heightCm) return percentiles[mid];
    if (grid_cm[mid] < heightCm) lo = mid;
    else hi = mid;
  }
  const t = (heightCm - grid_cm[lo]) / (grid_cm[hi] - grid_cm[lo]);
  return percentiles[lo] + t * (percentiles[hi] - percentiles[lo]);
}

// Normal CDF approximation for demo chart and fallback
function erf(x: number) {
  // Abramowitz and Stegun approximation
  const sign = Math.sign(x);
  x = Math.abs(x);
  const a1 = 0.254829592,
    a2 = -0.284496736,
    a3 = 1.421413741,
    a4 = -1.453152027,
    a5 = 1.061405429,
    p = 0.3275911;
  const t = 1 / (1 + p * x);
  const y = 1 -
    (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t) * Math.exp(-x * x);
  return sign * y;
}

export function normalCdf(x: number, mean: number, std: number) {
  return 0.5 * (1 + erf((x - mean) / (std * Math.SQRT2)));
}

export function makeDemoCdf(mean: number, std: number): CdfJson {
  const grid: number[] = [];
  const perc: number[] = [];
  for (let h = 140; h <= 205; h += 0.5) {
    grid.push(Number(h.toFixed(1)));
    perc.push(Number((normalCdf(h, mean, std) * 100).toFixed(1)));
  }
  const median = mean; // for symmetric demo
  return {
    grid_cm: grid,
    percentiles: perc,
    meta: {
      mean_cm: mean,
      median_cm: median,
      std_cm: std,
      n: 10000,
      note: "Demo data — not for publication",
    },
  };
}
